import {
  Badge,
  Box,
  Button,
  type ButtonProps,
  type ButtonStylesNames,
  Chip,
  createTheme,
  parseThemeColor,
  SegmentedControl,
  Switch,
  type CSSProperties,
  Stepper,
  Tabs,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { INK_SHADES } from "./colors";

export const ComponentsTheme = createTheme({
  components: {
    Badge: Badge.extend({
      defaultProps: {
        variant: "light",
        leftSection: (
          <Box
            component="span"
            w={5}
            h={5}
            bg="currentColor"
            style={{ borderRadius: "50%", display: "block" }}
          />
        ),
      },
    }),
    Button: Button.extend({
      defaultProps: {
        loaderProps: {
          size: 10,
        },
      },
      styles: (theme, props: ButtonProps) => {
        const parsedColor = parseThemeColor({
          color: props.color || theme.primaryColor,
          theme,
        });

        const defaultStyle: Partial<Record<ButtonStylesNames, CSSProperties>> =
          {
            section: {
              "&:active": {},
              "&:hover": {},
            },
            loader: {
              left: "auto",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              "--loader-color": "var(--mantine-color-ink-2)",
            },
            inner: {
              opacity: "1 !important",
              transform: "none !important",
            },
            label: {
              opacity: 1,
            },
            root: {
              height: "36px",
              paddingRight: "14px",
              paddingLeft: "14px",
              "&:active": {},
              "&:focus": {
                boxShadow: `0 0 0 3px var(--mantine-color-${parsedColor.color}-1)`,
              },
              "&:hover": {},
              "&[data-loading]": {},
              "&[data-disabled]": {},
              [`&[data-disabled] .${Button.classes.label}`]: {},
            },
          };

        switch (props.variant) {
          case "subtle":
            defaultStyle.loader!["--loader-color"] =
              `var(--mantine-color-ink-7)`;
            defaultStyle.root!["&[data-disabled]"].backgroundColor =
              "transparent";
            (
              defaultStyle.root![
                `&[data-disabled] .${Button.classes.label}`
              ] as CSSProperties
            ).color = `var(--mantine-color-ink-3)`;
            defaultStyle.label!.color = `var(--mantine-color-ink-7)`;
            defaultStyle.root!["&:active"].backgroundColor =
              `var(--mantine-color-ink-1)`;
            defaultStyle.root!["&:hover"].backgroundColor =
              `var(--mantine-color-ink-0)`;
            defaultStyle.section!.color = `var(--mantine-color-ink-5)`;
            break;
          case "default":
          case "outline":
            defaultStyle.loader!["--loader-color"] =
              `var(--mantine-color-ink-7)`;
            defaultStyle.root!["&:active"].backgroundColor =
              `var(--mantine-color-ink-1)`;
            defaultStyle.root!["&:hover"].backgroundColor =
              `var(--mantine-color-ink-0)`;
            break;
          case "light":
            defaultStyle.root!["&:active"].backgroundColor =
              `var(--mantine-color-${parsedColor.color}-1)`;
            break;
          case undefined:
          case "filled":
          default:
            defaultStyle.loader!["--loader-color"] =
              `var(--mantine-color-ink-2)`;
            defaultStyle.root!["&:active"].backgroundColor =
              `var(--mantine-color-${parsedColor.color}-7)`;
            defaultStyle.root!["&[data-loading]"].backgroundColor =
              `var(--mantine-color-${parsedColor.color}-6)`;
            defaultStyle.root!["&[data-loading]"].color =
              `var(--mantine-color-ink-2)`;
            break;
        }

        switch (props.size) {
          case "sm":
            defaultStyle.root!.height = "28px";
            defaultStyle.root!.paddingRight = "10px";
            defaultStyle.root!.paddingLeft = "10px";
            break;
          case "lg":
            defaultStyle.root!.height = "44px";
            defaultStyle.root!.paddingRight = "18px";
            defaultStyle.root!.paddingLeft = "18px";
            break;
          default:
            break;
        }

        return defaultStyle;
      },
    }),
    Chip: Chip.extend({
      defaultProps: {
        variant: "outline",
        icon: null,
      },
      styles: (theme, props) => {
        const defaultStyle = {
          label: {
            fontWeight: 500,
            borderColor: theme.colors.ink[2],
            color: theme.colors.ink[7],
          },
        };
        if (props.variant !== "outline") {
          return defaultStyle;
        }

        return {
          ...defaultStyle,
          label: {
            ...defaultStyle.label,
            "&[data-checked]": {
              backgroundColor: theme.colors.brand[0],
              borderColor: theme.colors.brand[6],
              color: theme.colors.brand[6],
            },
          },
        };
      },
    }),
    SegmentedControl: SegmentedControl.extend({
      defaultProps: {
        radius: "md",
      },
      styles: theme => ({
        root: {
          backgroundColor: theme.colors.ink[0],
          border: `1px solid ${theme.colors.ink[1]}`,
          padding: theme.spacing.s1,
        },
        indicator: {
          backgroundColor: theme.white,
          borderRadius: theme.radius.sm,
          boxShadow: theme.shadows.xs,
        },
        label: {
          color: theme.colors.ink[5],
          fontWeight: 500,
          padding: `6px ${theme.spacing.s4}`,
          "&[data-active]": {
            color: theme.colors.brand[6],
          },
        },
      }),
    }),
    Switch: Switch.extend({
      defaultProps: {
        labelPosition: "left",
      },
    }),
    Stepper: Stepper.extend({
      defaultProps: {
        iconSize: 24,
        completedIcon: <IconCheck size={14} />,
      },
      vars: (theme, props) => {
        if (props.variant === "flow") {
          if (props.orientation === "vertical") {
            return {
              root: {
                "--stepper-fz": theme.fontSizes.sm,
              },
              step: {
                "--separator-spacing": "1px", // default: xs / 2 ≈ 5px
              },
            };
          }

          return {
            root: {
              "--stepper-fz": theme.fontSizes.sm,
              "--stepper-outline-thickness": "1px",
            },
          };
        }

        return {
          root: {},
        };
      },
      styles: (theme, props) => {
        if (props.variant === "flow") {
          if (props.orientation === "vertical") {
            return {
              step: {
                // Allow to display box shadow on the step when it is active without cropping it
                paddingTop: 4,
                paddingInlineStart: 4,
                marginInlineStart: -4,
                // The last step has no min-height, so it also needs room below the icon
                "&:last-of-type": {
                  paddingBottom: 4,
                },
                fontWeight: 500,
                "&[data-progress]": {
                  fontWeight: 700,
                },
                // Reserves the bold width so the stepper doesn't grow when a step becomes active.
                // Requires the label to be wrapped in `<span data-text={label}>{label}</span>`.
                "& [data-text]": {
                  display: "block",
                },
                "& [data-text]::after": {
                  content: "attr(data-text)",
                  display: "block",
                  height: 0,
                  overflow: "hidden",
                  visibility: "hidden",
                  fontWeight: 700,
                  userSelect: "none",
                  pointerEvents: "none",
                },

                // `disabled` lands on the step's root <button>, not on the icon
                "&:disabled .mantine-Stepper-stepIcon": {
                  color: theme.colors.ink[3],
                  backgroundColor: INK_SHADES[25],
                  border: `1.5px dashed ${theme.colors.ink[2]}`,
                },
                "&:disabled .mantine-Stepper-stepLabel": {
                  color: theme.colors.ink[3],
                },
                "&:disabled .mantine-Stepper-stepDescription": {
                  color: theme.colors.ink[3],
                },
              },
              stepBody: {
                // Centers a lone label on the icon; a label + description is taller than the icon so it stays top-aligned
                alignSelf: "flex-start",
                minHeight: "var(--stepper-icon-size)",
                justifyContent: "center",
              },
              stepLabel: {
                textBox: "trim-both cap alphabetic",
                fontWeight: "inherit",
              },
              stepDescription: {
                color: theme.colors.ink[4],
                fontWeight: 400,
              },
              stepIcon: {
                // Mantine only marks steps before `active` as completed; `mod={{ done: true }}` on a step forces the same look
                "&[data-completed], [data-done]:not([data-progress]) &": {
                  color: theme.colors.success[8],
                  backgroundColor: theme.colors.success[0],
                  border: `1px solid ${theme.colors.success[5]}`,
                },
                "&[data-progress]": {
                  color: "white",
                  backgroundColor: theme.colors.brand[5],
                  boxShadow: `0 0 0 4px ${theme.colors.brand[1]}`,
                },
                // Paint the icon (and its progress ring) above the separator, which comes later in the DOM
                zIndex: 1,
                backgroundColor: "white",
                border: `1px solid ${theme.colors.ink[2]}`,
              },
              stepCompletedIcon: {
                color: theme.colors.success[8],
              },
              stepIconContent: {
                fontSize: theme.fontSizes.xs,
                fontWeight: 600,
              },
              verticalSeparator: {
                "&[data-active], [data-done]:not([data-progress]) &": {
                  borderColor: theme.colors.success[2],
                },
              },
            };
          }

          return {
            step: {
              color: theme.colors.ink[4],
              "&[data-progress]": {
                backgroundColor: theme.colors.brand[0],
                padding: "7px 14px 7px 10px",
                borderRadius: theme.radius.full,
                color: theme.colors.brand[5],
              },
              "&[data-completed]": {
                color: theme.colors.ink[7],
              },
            },
            stepIcon: {
              "&[data-progress]": {
                backgroundColor: "white",
                border: `2px solid ${theme.colors.brand[5]}`,
              },
            },
            stepIconContent: {
              display: "block",
              fontSize: theme.fontSizes.xs,
              fontWeight: 600,
              lineHeight: 1,
              textBox: "trim-both cap alphabetic",
            },
            stepLabel: {
              fontWeight: 500,
            },
            separator: {
              flex: "0 0 18px",
            },
          };
        }

        return {
          root: {},
        };
      },
    }),
    Tabs: Tabs.extend({
      styles: (theme, props) => {
        // Mantine has no `--sidebar` classes, so none of the default variant's borders apply
        if (props.variant === "sidebar") {
          return {
            list: {
              marginRight: theme.spacing.s6,
            },
            tab: {
              width: 200,
              paddingBlock: theme.spacing.s3,
              fontFamily: theme.headings.fontFamily,
              fontWeight: theme.fontWeights.medium,
              borderLeft: `2px solid ${theme.colors.gray[2]}`,
              color: theme.colors.ink[6],
              "&:hover, &[data-active]": {
                borderLeftColor: theme.colors.brand[5],
                backgroundColor: theme.colors.brand[0],
              },
              "&[data-active]": {
                color: theme.colors.brand[7],
              },
            },
            tabLabel: {
              textAlign: "left",
            },
          };
        }

        return {
          root: {},
        };
      },
    }),
  },
});
